import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Pencil, Plus, Sun } from "lucide-react";
import { useChains } from "../hooks/useChains";
import { useStaticSystemsById } from "../hooks/useStaticSystems";
import { useActiveChainId } from "../hooks/useActiveChainId";
import type { ChainSummary } from "../types/chain";
import { CreateChainModal } from "./CreateChainModal";
import { ChainSettingsModal } from "./ChainSettingsModal";

function chainLabel(
  chain: ChainSummary,
  staticSystems: ReturnType<typeof useStaticSystemsById>,
): ReactNode {
  const root = staticSystems.byId(chain.root_system_id);
  const text = `${chain.name} · ${root?.name ?? "?"} (${root?.class ?? "?"})`;
  if (chain.chain_type === "day_trip") {
    return (
      <>
        <Sun
          size={10}
          aria-label="Day Trip chain"
          className="chain-switcher__day-trip-badge"
        />
        {text}
      </>
    );
  }
  return text;
}

export function ChainSwitcher() {
  const chains = useChains();
  const staticSystems = useStaticSystemsById();
  const [urlChainId, setActiveChainId] = useActiveChainId();
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const sortedChains = useMemo(() => {
    if (!chains.data) return [];
    return [...chains.data].sort((a, b) => a.name.localeCompare(b.name));
  }, [chains.data]);

  const resolvedChain = useMemo<ChainSummary | null>(() => {
    if (sortedChains.length === 0) return null;
    if (urlChainId) {
      const match = sortedChains.find((c) => c.id === urlChainId);
      if (match) return match;
    }
    return sortedChains[0];
  }, [urlChainId, sortedChains]);

  useEffect(() => {
    if (!chains.data) return;
    if (urlChainId && chains.data.some((c) => c.id === urlChainId)) return;
    const fallback = sortedChains[0];
    setActiveChainId(fallback?.id ?? null);
  }, [chains.data, urlChainId, sortedChains, setActiveChainId]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const isEmpty = !chains.isLoading && sortedChains.length === 0;
  const label: ReactNode = chains.isLoading
    ? "…"
    : isEmpty || !resolvedChain
      ? (
          <>
            <Plus size={14} aria-hidden /> New Chain
          </>
        )
      : chainLabel(resolvedChain, staticSystems);

  const handleTriggerClick = () => {
    if (isEmpty) {
      setModalOpen(true);
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <div className="chain-switcher" ref={rootRef}>
      <span className="chain-switcher__prefix">Chain:</span>
      <button
        type="button"
        className="chain-switcher__trigger"
        onClick={handleTriggerClick}
        aria-expanded={open}
      >
        <span className="chain-switcher__label">{label}</span>
      </button>
      {resolvedChain?.is_owner && (
        <button
          type="button"
          className="chain-switcher__edit"
          onClick={() => setSettingsOpen(true)}
          aria-label="Chain settings"
        >
          <Pencil size={14} aria-hidden />
        </button>
      )}
      {open && (
        <div className="chain-switcher__dropdown">
          {sortedChains.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chain-switcher__item"
              onClick={() => {
                setActiveChainId(c.id);
                setOpen(false);
              }}
            >
              {chainLabel(c, staticSystems)}
            </button>
          ))}
          <div className="chain-switcher__sep" />
          <button
            type="button"
            className="chain-switcher__item chain-switcher__item--new"
            onClick={() => {
              setOpen(false);
              setModalOpen(true);
            }}
          >
            <Plus size={14} aria-hidden /> New Chain
          </button>
        </div>
      )}
      {modalOpen && <CreateChainModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        ownedRootSystemIds={
          chains.data
            ?.filter((c) => c.is_owner)
            .reduce<Map<string, string>>((m, c) => {
              m.set(c.root_system_id, c.name);
              return m;
            }, new Map()) ?? new Map()
        }
        onCreated={(created) => {
          setActiveChainId(created.id);
          setModalOpen(false);
        }}
      />}
      {settingsOpen && resolvedChain && (
        <ChainSettingsModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          chain={resolvedChain}
          ownedRootSystemIds={
            chains.data
              ?.filter((c) => c.is_owner && c.id !== resolvedChain.id)
              .reduce<Map<string, string>>((m, c) => {
                m.set(c.root_system_id, c.name);
                return m;
              }, new Map()) ?? new Map()
          }
          onUpdated={() => setSettingsOpen(false)}
          onDeleted={() => {
            const fallback = sortedChains.find(
              (c) => c.id !== resolvedChain.id,
            );
            setActiveChainId(fallback?.id ?? null);
            setSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}
